import { APIFieldAttributes, ArdoqId } from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { fieldOperations } from '@ardoq/core';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getComponentLabelParts,
  getFullComponentLabelWithLegacyFormatting,
} from '@ardoq/graph';
import type { WorkspaceAndModelLookup } from '@ardoq/data-model';
import { ComponentsState } from 'streams/components/components$';
import { contrastEnsuredComponentFill } from 'utils/modelCssManager/getCssColors';
import type {
  ComponentPathData,
  ComponentPathSegment,
  ReferenceLabel,
} from 'tabview/pagesView/types';

export const getFieldsWithDateRanges = (
  fields: APIFieldAttributes[]
): APIFieldAttributes[] =>
  dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(fields).fields;

export const getComponentFieldsOfWorkspace = (
  workspaceFields: readonly APIFieldAttributes[]
) =>
  dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(
    workspaceFields.filter(fieldOperations.appliesToComponents)
  ).fields;

const collectAncestorIds = (
  componentId: ArdoqId,
  components: ComponentsState
): ArdoqId[] => {
  const parentId = components.byId[componentId]?.parent;
  if (!parentId) return [componentId];
  return [...collectAncestorIds(parentId, components), componentId];
};

// Zero-arg `getComponentLabelParts(id)` falls back to the cached
// backbone-backed labels interface. The pure overload exists, but feeding it
// requires plumbing an EnhancedScopeData + formattingFilters stream into
// viewModel$. Tracked separately; not fixing here.
const toPathSegment = (
  id: ArdoqId,
  components: ComponentsState
): ComponentPathSegment => ({
  _id: id,
  name: components.byId[id]?.name ?? '',
  label: getComponentLabelParts(id),
});

export const buildComponentPath = (
  componentId: ArdoqId,
  components: ComponentsState,
  workspaceAndModel: WorkspaceAndModelLookup
): ComponentPathData | null => {
  const component = components.byId[componentId];
  if (!component) return null;
  const workspace = workspaceAndModel.workspacesById[component.rootWorkspace];
  if (!workspace) return null;
  return {
    workspace: { _id: workspace._id, name: workspace.name },
    path: collectAncestorIds(componentId, components).map(id =>
      toPathSegment(id, components)
    ),
  };
};

const toComponentPathEntry =
  (components: ComponentsState, workspaceAndModel: WorkspaceAndModelLookup) =>
  (id: ArdoqId): [ArdoqId, ComponentPathData] | null => {
    const data = buildComponentPath(id, components, workspaceAndModel);
    return data ? [id, data] : null;
  };

export const getComponentPathById = (
  componentIds: ArdoqId[],
  referenceLabelById: Map<ArdoqId, ReferenceLabel>,
  components: ComponentsState,
  workspaceAndModel: WorkspaceAndModelLookup
): Map<ArdoqId, ComponentPathData> => {
  const ids = new Set([
    ...componentIds,
    ...[...referenceLabelById.values()].flatMap(({ source, target }) => [
      source,
      target,
    ]),
  ]);
  return new Map(
    [...ids]
      .map(toComponentPathEntry(components, workspaceAndModel))
      .filter(isTruthy)
  );
};

export const getComponentDisplayLabelById = (
  componentIds: ArdoqId[]
): Map<ArdoqId, string> =>
  new Map(
    componentIds.map(id => [
      id,
      getFullComponentLabelWithLegacyFormatting(getComponentLabelParts(id)),
    ])
  );

export const getComponentFillById = (
  componentIds: ArdoqId[]
): Map<ArdoqId, string> =>
  new Map(componentIds.map(id => [id, contrastEnsuredComponentFill(id)]));
